import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Save } from 'lucide-react';
import { PaymentProjectSnapshot } from '@/types/project';

interface EditSnapshotDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  snapshot: PaymentProjectSnapshot | null;
  onSave: (snapshotId: string, updates: Partial<PaymentProjectSnapshot>) => Promise<void>;
}

export function EditSnapshotDialog({
  open,
  onOpenChange,
  snapshot,
  onSave,
}: EditSnapshotDialogProps) {
  const [revenue, setRevenue] = useState<number>(0);
  const [profitMargin, setProfitMargin] = useState<number>(0);
  const [operationsOverheadPct, setOperationsOverheadPct] = useState<number>(0);
  const [operationsCompensationPerOwner, setOperationsCompensationPerOwner] = useState<number>(0);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when snapshot changes
  useEffect(() => {
    if (snapshot) {
      setRevenue(snapshot.revenue || 0);
      setProfitMargin(snapshot.profit_margin || 0);
      setOperationsOverheadPct(snapshot.operations_overhead_pct || 0);
      setOperationsCompensationPerOwner(snapshot.operations_compensation_per_owner || 0);
      setError(null);
    }
  }, [snapshot]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Calculate derived values
  const grossProfit = revenue * (profitMargin / 100);
  const operationsOverheadCost = revenue * (operationsOverheadPct / 100);

  const handleSave = async () => {
    if (!snapshot) return;

    setIsSaving(true);
    setError(null);

    try {
      await onSave(snapshot.id, {
        revenue,
        profit_margin: profitMargin,
        operations_overhead_pct: operationsOverheadPct,
        operations_compensation_per_owner: operationsCompensationPerOwner,
        calculated_gross_profit: grossProfit,
      });
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  if (!snapshot) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Historical Snapshot</DialogTitle>
          <DialogDescription>
            Update the financial data for <strong>{snapshot.project_name}</strong> in this payment period.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="revenue">Revenue</Label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-muted-foreground">$</span>
                <Input
                  id="revenue"
                  type="number"
                  value={revenue}
                  onChange={(e) => setRevenue(Number(e.target.value))}
                  className="pl-7"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="profit-margin">Profit Margin</Label>
              <div className="relative">
                <Input
                  id="profit-margin"
                  type="number"
                  value={profitMargin}
                  onChange={(e) => setProfitMargin(Number(e.target.value))}
                  className="pr-7"
                  min={0}
                  max={100}
                />
                <span className="absolute right-3 top-2.5 text-muted-foreground">%</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ops-overhead">Operations Overhead</Label>
              <div className="relative">
                <Input
                  id="ops-overhead"
                  type="number"
                  value={operationsOverheadPct}
                  onChange={(e) => setOperationsOverheadPct(Number(e.target.value))}
                  className="pr-7"
                  min={0}
                  max={100}
                />
                <span className="absolute right-3 top-2.5 text-muted-foreground">%</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ops-comp">Ops Compensation/Owner</Label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-muted-foreground">$</span>
                <Input
                  id="ops-comp"
                  type="number"
                  value={operationsCompensationPerOwner}
                  onChange={(e) => setOperationsCompensationPerOwner(Number(e.target.value))}
                  className="pl-7"
                />
              </div>
            </div>
          </div>

          {/* Calculated Values Preview */}
          <div className="p-3 bg-muted/50 rounded-lg">
            <h4 className="text-sm font-medium mb-2">Calculated Values</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Gross Profit:</span>
                <span className="font-medium font-serif text-positive">{formatCurrency(grossProfit)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Ops Overhead Cost:</span>
                <span className="font-medium font-serif">{formatCurrency(operationsOverheadCost)}</span>
              </div>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
